import classNames from "classnames";
import { t } from "i18next";
import { useState } from "react";
import { useSelector } from "react-redux";

import { useHandleService } from "../../hooks/useHandleService";
import { splitDateTime } from "../../services/DateTimeService";
import { RootState } from "../../store";
import { checkIsPaused, checkIsRunning } from "../../store/clock-slice";
import Modal from "../Modal/Modal";
import Clock from "../Timer/Clock";

import styles from "./appointment-details.module.css";

type AppointmentDetailsProps = {
  isServicesOverview?: boolean;
  isServicesCompletion?: boolean;
  isDeficiencyOverview?: boolean;
  deficiency?: boolean;
  showMangelTag?: boolean;
  deficiencyDate?: string;
  lastEventDate?: string | null;
  hideClock?: boolean;
};

export const AppointmentDetails = ({
  isServicesOverview = false,
  isServicesCompletion = false,
  isDeficiencyOverview = false,
  showMangelTag = false,
  deficiency = false,
  deficiencyDate,
  lastEventDate = null,
  hideClock = false,
}: AppointmentDetailsProps): JSX.Element => {
  const { handlePauseService, handleResumeService } = useHandleService();

   const [isPauseModalOpen, setPauseModalOpen] = useState<boolean>(false);

  const [isResumeModalOpen, setResumeModalOpen] = useState<boolean>(false);
  
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const appointment = useSelector((state: RootState) => state.appointment);
  const overviewAppointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );

  const selectedAppointment =
    (deficiency || isServicesOverview) && overviewAppointment.id !== ""
      ? overviewAppointment
      : appointment;

  const clockAppointment = deficiency && overviewAppointment.id !== ""
      ? overviewAppointment
      : appointment;

  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, clockAppointment.id, user!.id)
  );

   const isPaused = useSelector((state: RootState) => checkIsPaused(state, clockAppointment.id, user!.id)
  );

  const task = useSelector((state: RootState) => state.task);

  const isTaskValid = task && task.customer !== "";
  const object =
    isTaskValid && !isServicesOverview ? task : selectedAppointment;

  const date = deficiencyDate
    ? splitDateTime(deficiencyDate).date
    : isTaskValid
      ? task.date.split(".").slice(0, 2).join(".") + "."
      : splitDateTime(selectedAppointment?.plannedStart ?? "").date || null;

  const isDeficiency =
    selectedAppointment.incidents.length > 0 || showMangelTag;

  const typeOfAppointment =
    object?.typeOfAppointment === "auftrag"
      ? "Bekämpfung"
      : object?.typeOfAppointment === "other"
      ? "Sonstiges"
      : object?.typeOfAppointment;

  const classes = classNames(styles["task-details__card"], {
    [styles["task-details__card--green"]]: isRunning,
    [styles["task-details__card--white"]]: !isRunning,
  });

  const onClickClock = () => {
    if (isRunning) {
      setPauseModalOpen(true);
      return;
    }

    if (isPaused) {
      setResumeModalOpen(true);
      return;
    }
  };

  return (
    <div className={classes}>
      <Modal
        isOpen={isPauseModalOpen}
        message={t("PauseService")}
        note="PauseMessage"
        iconType="pause"
        onConfirm={async () => {
          await handlePauseService();
          setPauseModalOpen(false);
        }}
        onCancel={() => setPauseModalOpen(false)}
        cancelText="Cancel"
        confirmText="Pause"
        confirmButtonColor="#F29100"
        closeButtonColor="#EDEFF5"
        cancelTextColor="#3D5170"
      />
      <Modal
        isOpen={isResumeModalOpen}
        message={t("ResumeService")}
        note="ResumeMessage"
        iconType="play"
        onConfirm={async () => {
          await handleResumeService();
          setResumeModalOpen(false);
        }}
        onCancel={() => setResumeModalOpen(false)}
        cancelText="Cancel"
        confirmText="Resume"
        confirmButtonColor="#11A8B8"
        closeButtonColor="#EDEFF5"
        cancelTextColor="#3D5170"
        
      />
      {!hideClock && (isRunning || isPaused) ? <Clock appointmentId={clockAppointment.id} pulledUp onClick={onClickClock} /> : null}

      {isServicesOverview && (
        <div className={styles["task-details__service-summary"]}>
          <span>{t("ServiceSummary")}</span>
        </div>
      )}

      {isDeficiencyOverview && (
        <div className={styles["task-details__service-summary"]}>
          <span>{t("DeficiencySummary")}</span>
        </div>
      )}

      {isServicesCompletion && (
        <div className={styles["task-details__service-summary"]}>
          <span>{t("Completion of service")}</span>
        </div>
      )}

      <button className={styles["task-details__circle"]}>
        {lastEventDate ? lastEventDate : date}
      </button>

      <div className={styles["task-details__task"]}>
        <h3 className={styles["task-details__titles"]}>
          {object?.additionalData?.customer.name}
        </h3>

        <div>
          {object?.additionalData?.property.name && (
            <h4
              className={`${styles["task-details__infos"]} ${styles["task-details__infos-bold"]}`}
            >
              {object?.additionalData?.property.name}
            </h4>
          )}
          <h4 className={styles["task-details__infos"]}>
            {object?.additionalData?.property.address}
          </h4>
        </div>
        {typeOfAppointment && (
          <button
            className={`${styles["task-details__button"]} ${
              isDeficiency && styles["task-details__button--orange"]
            }`}
          >
            {isDeficiency ? t("Deficiency") : typeOfAppointment}
          </button>
        )}
      </div>
    </div>
  );
};
